import type { Quote, QuoteItem, QuoteOrderLine, QuoteOrderTranche, QuoteSupplementRef, ServiceItem } from '../types';
import type { LvPlanStep } from './quoteOrderTranches';
import {
    formatEur,
    formatQty,
    isQuoteOrdered,
    paketErhoehungGesperrt,
    planTrancheForProjectLv,
} from './quoteOrderTranches';
import { activeQuoteItems, mapQuoteItemsToServiceItems } from './quoteLogic';
import { isDailyUnit, isHourlyUnit, isMonthlyUnit } from './serviceUnits';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * NACHTRAGSANGEBOT — Rechenkern (rein: kein Netz, keine Uhr)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Fall: zu einem laufenden Projekt beauftragt der Kunde mehr — z. B. 20
 * weitere Begehungen zu den 20 beauftragten. Das Nachtragsangebot legt KEIN
 * neues Projekt an, seine Positionen fließen ins Projekt-LV:
 *
 *  - gleiche Leistung zum gleichen Einzelpreis → die Vertragsmenge der
 *    vorhandenen Position steigt (20 → 40). Erbracht, abgerechnet und
 *    Verzicht bleiben, wie sie sind; kein Journaleintrag.
 *  - abweichender Einzelpreis (oder Einheit, oder Paketposition über Menge 1)
 *    → eigene NEUE Position. Kumulative Rechnungen rechnen Menge × Einzelpreis:
 *    ein neuer Preis an der alten Position bewertete die schon abgerechnete
 *    Menge neu (Betreiber-Entscheid 1).
 *  - neue Positionen tragen die LV-Nr. „N<seq>.<lfd>" (Betreiber-Entscheid 2).
 *
 * Gerechnet wird mit demselben Kern wie die Teilbeauftragung
 * (quoteOrderTranches.planTrancheForProjectLv, Optionen-Parameter) — nur die
 * Zuordnung ist hier nötig, weil Nachtragspositionen eigene ids tragen.
 *
 * VERTRAG — Signaturen sind fest (Pakete B/C rufen sie auf).
 */

/** Toleranz beim Einzelpreisvergleich — wie bei der Teilbeauftragung. */
const PRICE_EPS = 0.005;

const num = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const day = (iso?: string | null): string => (iso ? String(iso).slice(0, 10) : '');

const formatDay = (iso?: string | null): string => {
    const d = day(iso);
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : '';
};

/** Ziel einer Nachtragsposition im Projekt-LV. */
export type SupplementTarget =
    | { kind: 'increase'; serviceId: string }
    | { kind: 'append' };

/** Zuordnung je Nachtragsposition (Schlüssel: QuoteItem.id). */
export type SupplementMapping = Record<string, SupplementTarget>;

/** Ein Schritt im Projekt-LV für einen Nachtrag. */
export interface SupplementPlanStep extends LvPlanStep {
    /**
     * `increase`: Projektposition, deren Vertragsmenge steigt. `append`: id
     * der neuen Position (in aller Regel = itemId; abweichend nur, wenn die
     * eigene id im Projekt schon vergeben war).
     */
    serviceId?: string;
    /**
     * Gewählt war `increase`, angelegt wird eine neue Position — mit Grund
     * (Einzelpreis anders, Einheit anders, Paketposition über Menge 1).
     */
    forcedAppendReason?: string;
}

export interface SupplementPlan {
    /** Projekt-LV nach dem Nachtrag. */
    services: ServiceItem[];
    steps: SupplementPlanStep[];
    /** ids der neu angehängten Positionen (Monatspauschale: ja/nein-Entscheidung). */
    newServiceIds: string[];
    /**
     * Nachtragsposition → Projektposition, auf der JEDE Zeile landet:
     * erhöhte Position, sonst die neue (in aller Regel die eigene id; selten
     * eine abgeleitete). NEP nicht. Geht an den Auftragsteil und ist dort
     * VERBINDLICH — spätere Teile und „Erneut eintragen" prüfen nur noch diese
     * Position, keinen Vorschlag.
     */
    serviceIdByItemId: Record<string, string>;
    /** Zeilen ohne (aktive) Angebotsposition — sperren das Eintragen. */
    missing: string[];
    /** Zugeordnete Projektposition existiert nicht (mehr) — sperrt das Eintragen. */
    missingTargets: { itemId: string; serviceId: string }[];
}

/** Ist das Angebot ein Nachtrag zu einem bestehenden Projekt? */
export const isSupplementQuote = (q: Pick<Quote, 'details'> | null | undefined): boolean =>
    !!q?.details?.nachtragZu?.projectId;

const projectText = (ref: Pick<QuoteSupplementRef, 'projectNumber' | 'projectName'>): string =>
    [(ref.projectNumber || '').trim(), (ref.projectName || '').trim()].filter(Boolean).join(' ');

const validSeq = (seq: unknown): number | null => {
    const n = Number(seq);
    return Number.isInteger(n) && n > 0 ? n : null;
};

/** Anzeige „Nachtrag 2 zu 26031030 Neubau Halle" (ohne Nummer: „Nachtrag zu …"). */
export const supplementLabel = (ref: QuoteSupplementRef): string => {
    const seq = validSeq(ref?.seq);
    return `Nachtrag${seq ? ` ${seq}` : ''} zu ${projectText(ref || { projectId: '' }) || 'Projekt'}`;
};

/** LV-Nummer einer neu angehängten Nachtragsposition: `N<seq>.<index+1>`. */
export const supplementLvPosition = (seq: number, index: number): string => `N${seq}.${index + 1}`;

/** Nächste Nachtragsnummer: höchste vorhandene + 1 (leer → 1). */
export const nextSupplementSeq = (existingSeqs: Array<number | null | undefined>): number =>
    (existingSeqs || []).reduce<number>((max, s) => Math.max(max, validSeq(s) ?? 0), 0) + 1;

/**
 * Höchste laufende Nummer x der Positionen „N<seq>.x" im Projekt-LV (0 =
 * keine). Spätere Teile hängen mit x+1 an — die Nummern bleiben fortlaufend,
 * auch wenn dazwischen eine Position gelöscht wurde, und kollidieren nie.
 */
export const highestSupplementLvIndex = (services: Pick<ServiceItem, 'lvPosition'>[], seq: number): number => {
    const re = new RegExp(`^N${seq}\\.(\\d+)$`, 'i');
    return (services || []).reduce((max, s) => {
        const m = re.exec(String(s?.lvPosition ?? '').trim());
        return m ? Math.max(max, Number(m[1])) : max;
    }, 0);
};

/**
 * Angebotsposition aus einer Projektposition (Editor: „Projektposition
 * übernehmen"): Titel, Einheit, LV-Nr., Kategorie, Zuordnungsart und
 * Einzelpreis aus dem Projekt, Menge 1, `targetServiceId` = svc.id.
 */
export const quoteItemFromProjectService = (svc: ServiceItem, id: string): QuoteItem => ({
    id,
    lvPosition: svc.lvPosition || '',
    title: svc.title || '',
    unit: svc.unit || '',
    quantity: 1,
    unitPrice: num(svc.unitPrice),
    ...(svc.category ? { category: svc.category } : {}),
    ...(svc.assignment ? { assignment: svc.assignment } : {}),
    targetServiceId: svc.id,
});

/**
 * Einheit für den Vergleich: Schreibweisen derselben Einheit gelten gleich
 * („Stk." = „Stück" = „St", „Std" = „h" = „Stunden", „pau" = „psch"). Leer
 * zählt als Stück — so liest der Projekt-Lader eine Position ohne Einheit.
 */
const unitKey = (unit?: string | null): string => {
    const u = String(unit ?? '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/\.$/, '');
    if (!u || ['stk', 'stück', 'stueck', 'st', 'begehung', 'begehungen'].includes(u)) return 'stk';
    if (isHourlyUnit(u)) return 'std';
    if (isMonthlyUnit(u)) return 'monat';
    if (isDailyUnit(u)) return 'tag';
    if (['pau', 'psch', 'pschl', 'pauschal', 'pauschale', 'pa'].includes(u)) return 'psch';
    return u;
};

/** Effektiver Einzelpreis der Angebotsposition im Projekt (nach Positionsrabatt, wie die Annahme). */
const projectUnitPrice = (item: QuoteItem, quantity: number): number =>
    mapQuoteItemsToServiceItems([{ ...item, excluded: false, quantity }])[0]?.unitPrice ?? num(item.unitPrice);

/**
 * Warum eine Nachtragsposition die Projektposition NICHT erhöhen kann —
 * null = Erhöhung zulässig. Gründe: Einzelpreis weicht ab (> 0,005 €),
 * Einheit weicht ab, Paketposition würde über Menge 1 steigen.
 *
 * Dazu, über die drei hinaus: eine Eventualposition (NEP) im Projekt ist
 * nicht beauftragt — eine beauftragte Nachtragsmenge darauf stünde weiter als
 * „nicht beauftragt" da und verlangte bei jeder Buchung eine Begründung.
 */
export const supplementIncreaseBlocker = (
    svc: ServiceItem,
    item: QuoteItem,
    addQuantity: number,
): string | null => {
    const ep = projectUnitPrice(item, num(addQuantity) || 1);
    if (Math.abs(num(svc.unitPrice) - ep) > PRICE_EPS) {
        return `Einzelpreis ${formatEur(num(svc.unitPrice))} im Projekt, ${formatEur(ep)} im Nachtrag`;
    }
    if (unitKey(svc.unit) !== unitKey(item.unit)) {
        return `Einheit „${(svc.unit || '').trim() || '–'}“ im Projekt, „${(item.unit || '').trim() || '–'}“ im Nachtrag`;
    }
    if (paketErhoehungGesperrt(svc, num(svc.quantityContracted) + num(addQuantity))) {
        return `Paketposition ‚${svc.title || svc.lvPosition || 'Position'}‘ gibt es nur mit Menge 1`;
    }
    if (svc.is_optional) {
        return 'Eventualposition (NEP) im Projekt — beauftragte Menge wird eigene Position';
    }
    return null;
};

/** Titel für den Vergleich: klein, ohne Satzzeichen, Leerraum vereinheitlicht. */
const titleKey = (title?: string | null): string =>
    String(title ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/**
 * Vorschlag für die Zuordnung je aktiver, nicht-NEP Nachtragsposition:
 * 1. `targetServiceId`, solange die Projektposition existiert;
 * 2. sonst eine EINDEUTIGE Projektposition mit gleicher Einheit und gleichem
 *    normalisiertem Titel (oder gleicher Einheit + gleicher assignment
 *    'inspection' bei genau einer Begehungsposition);
 * 3. sonst `append`.
 * Der Preis entscheidet hier nicht — das tut der Plan (Erhöhung wird dort zur
 * neuen Position, siehe forcedAppendReason).
 * Eventualpositionen (NEP) des Projekts werden nicht vorgeschlagen — nur von
 * Hand gewählt (dann entscheidet supplementIncreaseBlocker).
 */
export const proposeSupplementMapping = (
    services: ServiceItem[],
    items: QuoteItem[],
): SupplementMapping => {
    const list = Array.isArray(services) ? services : [];
    const byId = new Map(list.map((s) => [String(s.id), s]));
    const candidates = list.filter((s) => !s.is_optional);
    const inspections = candidates.filter((s) => s.assignment === 'inspection');
    const out: SupplementMapping = {};
    for (const item of activeQuoteItems(items || [])) {
        if (item.nep) continue;
        if (item.targetServiceId && byId.has(item.targetServiceId)) {
            out[item.id] = { kind: 'increase', serviceId: item.targetServiceId };
            continue;
        }
        const unit = unitKey(item.unit);
        const title = titleKey(item.title);
        const sameTitle = title
            ? candidates.filter((s) => unitKey(s.unit) === unit && titleKey(s.title) === title)
            : [];
        if (sameTitle.length === 1) {
            out[item.id] = { kind: 'increase', serviceId: String(sameTitle[0].id) };
            continue;
        }
        if (sameTitle.length === 0 && item.assignment === 'inspection'
            && inspections.length === 1 && unitKey(inspections[0].unit) === unit) {
            out[item.id] = { kind: 'increase', serviceId: String(inspections[0].id) };
            continue;
        }
        out[item.id] = { kind: 'append' };
    }
    return out;
};

/**
 * Was ein Nachtrag im Projekt-LV ändert.
 *  - `increase` → Vertragsmenge der Projektposition + Menge (erbracht,
 *    abgerechnet, Verzicht unberührt), es sei denn supplementIncreaseBlocker
 *    greift: dann neue Position mit forcedAppendReason.
 *  - `append` / ohne Eintrag → neue Position wie bei der Annahme
 *    (mapQuoteItemsToServiceItems), LV-Nr. supplementLvPosition(seq, n).
 *  - NEP-Positionen des Angebots werden (nur beim ersten Teil, d. h. wenn
 *    `includeNep`) als is_optional angehängt, ebenfalls mit N-Präfix.
 *  - Steht eine Position mit der id der Angebotsposition schon im Projekt
 *    (späterer Teil), wird sie erhöht statt doppelt angehängt.
 *  - `priorServiceIdByItemId`: Zuordnung aus früheren Teilen dieses
 *    Angebots — hat Vorrang vor `mapping`. Sie ist verbindlich: steht eine
 *    Position dort, zählt `mapping` für sie nicht mehr (eine Angebotsposition
 *    landet nie auf zwei Projektpositionen). Zeigt sie auf die eigene id, die
 *    noch nicht im Projekt steht (früherer Teil noch nicht eingetragen), wird
 *    unter dieser id angehängt.
 * Die N-Nummern laufen über alle in diesem Plan angehängten Positionen und
 * beginnen hinter der höchsten „N<seq>.x" im Projekt.
 */
export const planSupplementForProjectLv = (
    services: ServiceItem[],
    quoteItems: QuoteItem[],
    lines: QuoteOrderLine[],
    mapping: SupplementMapping,
    opts: { seq: number; includeNep: boolean; priorServiceIdByItemId?: Record<string, string> },
): SupplementPlan => {
    const mapped: Record<string, string> = {};
    for (const [itemId, t] of Object.entries(mapping || {})) {
        if (t?.kind === 'increase' && t.serviceId) mapped[itemId] = t.serviceId;
    }
    const base = highestSupplementLvIndex(services || [], opts.seq);
    const plan = planTrancheForProjectLv(services || [], quoteItems || [], lines, {
        priorServiceIdByItemId: opts.priorServiceIdByItemId || {},
        mappedServiceIdByItemId: mapped,
        increaseBlocker: supplementIncreaseBlocker,
        appendLvPosition: (i) => supplementLvPosition(opts.seq, base + i),
        includeNep: opts.includeNep,
    });
    return {
        services: plan.services,
        steps: plan.steps,
        newServiceIds: plan.newServiceIds,
        serviceIdByItemId: plan.serviceIdByItemId,
        missing: plan.missing,
        missingTargets: plan.missingTargets,
    };
};

/**
 * Zuordnung aller bisherigen Teile eines Angebots (Union von serviceIdByItemId).
 * Nur beauftragte Teile, nach laufender Nummer — ein späterer Teil gewinnt.
 */
export const priorSupplementServiceIds = (tranches: QuoteOrderTranche[] | null | undefined): Record<string, string> => {
    const out: Record<string, string> = {};
    const ordered = (Array.isArray(tranches) ? tranches : [])
        .filter((t) => t && t.kind === 'order')
        .sort((a, b) => num(a.seq) - num(b.seq));
    for (const t of ordered) {
        for (const [itemId, serviceId] of Object.entries(t.serviceIdByItemId || {})) {
            if (itemId && serviceId) out[itemId] = String(serviceId);
        }
    }
    return out;
};

/** Menge mit Einheit, ohne Leerzeichen am Ende bei fehlender Einheit. */
const qtyUnit = (qty: number, unit?: string): string => {
    const u = (unit || '').trim();
    return `${formatQty(qty)}${u ? ` ${u}` : ''}`;
};

/**
 * Text der Projektnotiz, z. B. „Nachtrag N1 aus Angebot A-… (Auftrag AB …,
 * Bestellung …) vom 23.10.2026: Begehung SiGeKo 20 → 40 Stk; neu: N1.1
 * Baustellenordnung 1 psch. Netto 6.400,00 €."
 * Spätere Teile nennen ihre Nummer („Nachtrag N1 Teil 2 …"). Hält im Projekt
 * fest, was woher kam — auch für Koordinatoren (quotes ist admin-only).
 */
export const supplementNoteText = (
    quote: Pick<Quote, 'quoteNumber'>,
    tranche: QuoteOrderTranche,
    plan: Pick<SupplementPlan, 'steps'>,
    opts: { seq: number; orderNumber?: string },
): string => {
    const refs = [
        opts.orderNumber ? `Auftrag ${opts.orderNumber}` : '',
        tranche.customerRef ? `Bestellung ${tranche.customerRef}` : '',
    ].filter(Boolean);
    const date = formatDay(tranche.orderDate || tranche.at);
    const head = `Nachtrag N${opts.seq}${num(tranche.seq) > 1 ? ` Teil ${tranche.seq}` : ''} aus Angebot ${quote.quoteNumber}`
        + `${refs.length ? ` (${refs.join(', ')})` : ''}${date ? ` vom ${date}` : ''}`;
    const steps = plan.steps || [];
    const raised = steps
        .filter((s) => s.kind === 'increase')
        .map((s) => `${(s.title || '').trim()} ${formatQty(s.from)} → ${qtyUnit(s.to, s.unit)}`.trim());
    const added = steps
        .filter((s) => s.kind === 'append' && !s.nep)
        .map((s) => `${s.lvPosition ? `${s.lvPosition} ` : ''}${(s.title || '').trim()} ${qtyUnit(s.to, s.unit)}`.trim());
    const nep = steps
        .filter((s) => s.kind === 'append' && s.nep)
        .map((s) => `${s.lvPosition ? `${s.lvPosition} ` : ''}${(s.title || '').trim()} ${qtyUnit(s.to, s.unit)}`.trim());
    const parts = [
        ...raised,
        ...(added.length ? [`neu: ${added.join(', ')}`] : []),
        ...(nep.length ? [`NEP: ${nep.join(', ')}`] : []),
    ];
    const body = parts.length ? `${head}: ${parts.join('; ')}` : head;
    return `${body}${body.endsWith('.') ? '' : '.'} Netto ${formatEur(tranche.netAmount)}.${tranche.note ? ` ${tranche.note}` : ''}`;
};

/**
 * Hauptangebot eines Projekts aus einer Angebotsliste: beauftragt
 * (accepted/partial), project_id = projectId, KEIN Nachtrag; das älteste.
 * Ohne solches: irgendein Angebot mit project_id = projectId (auch Nachtrag) —
 * gewöhnliche vor Nachträgen, dann das älteste.
 */
export const pickMainQuote = <T extends Pick<Quote, 'projectId' | 'status' | 'details' | 'acceptedAt' | 'createdAt'>>(
    quotes: T[],
    projectId: string,
): T | undefined => {
    // Ohne Datum ganz hinten — ein Altbestand ohne Zeitstempel ist kein „ältestes".
    const when = (q: T): string => q.acceptedAt || q.createdAt || '￿';
    const oldest = (list: T[]): T | undefined =>
        [...list].sort((a, b) => (when(a) < when(b) ? -1 : when(a) > when(b) ? 1 : 0))[0];
    const own = (quotes || []).filter((q) => !!projectId && q?.projectId === projectId);
    const main = oldest(own.filter((q) => isQuoteOrdered(q.status) && !isSupplementQuote(q)));
    if (main) return main;
    return oldest(own.filter((q) => !isSupplementQuote(q))) ?? oldest(own);
};

/**
 * Bezugszeile im Angebots-PDF unter der Angebotsart:
 * „zu Auftrag AB 12-2026 vom 01.03.2026 · Projekt 26031030 Neubau Halle".
 * Fehlende Teile entfallen samt Trenner; ganz ohne Angaben ''.
 */
export const supplementPdfReferenceLine = (ref: QuoteSupplementRef): string => {
    if (!ref) return '';
    const orderNumber = (ref.mainOrderNumber || '').trim();
    const orderDate = formatDay(ref.mainOrderDate);
    const order = orderNumber || orderDate
        ? `zu Auftrag${orderNumber ? ` ${orderNumber}` : ''}${orderDate ? ` vom ${orderDate}` : ''}`
        : '';
    const project = projectText(ref);
    return [order, project ? `Projekt ${project}` : ''].filter(Boolean).join(' · ');
};
